import React from 'react';
import {
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
} from "@mui/material"; 
import { City, Country, State } from '../../../types/countryStateCity';

export interface BusinessAddress {
  street?: string;
  cityId?: number;
  stateId?: number;
  countryId?: number;
  zipcode?: string;
}

interface BusinessAddressFormProps {
  disabled: boolean;
  businessAddress: BusinessAddress | undefined;
  countries: Country[];
  setBusinessAddress: React.Dispatch<
    React.SetStateAction<BusinessAddress | undefined>
  >;
  setActiveCountry: (country: Country) => void;
}

const BusinessAddressForm: React.FC<BusinessAddressFormProps> = ({
  disabled,
  businessAddress,
  countries,
  setBusinessAddress,
  setActiveCountry,
}) => {
  const [selectedCountry, setSelectedCountry] = React.useState<Country | null>(
    null
  );
  const [selectedState, setSelectedState] = React.useState<State | null>(null);
  const [selectedCity, setSelectedCity] = React.useState<City | null>(null);

  React.useEffect(() => {
    if (businessAddress) {
      const { countryId, stateId, cityId } = businessAddress;
      const country =
        (countries &&
          countries?.find((c) => Number(c.id) === Number(countryId))) ||
        null;
      const state =
        country?.states?.find((s) => Number(s.id) === Number(stateId)) || null;
      const city =
        state?.cities?.find((c) => Number(c.id) === Number(cityId)) || null;

      setSelectedCountry(country);
      country && setActiveCountry(country);
      setSelectedState(state);
      setSelectedCity(city);
    }
  }, [countries]);

// BusinessAddressForm.tsx
const handleCountryChange = (bookingType: SelectChangeEvent<string>) => {
  // Convert the selected value to a number
  const countryId = Number(bookingType.target.value);
  
  // Find the corresponding country from the passed in countries list
  const country = countries?.find((c: Country) => Number(c.id) === countryId);
  
  // Update local state for the selected country in the BusinessAddressForm component
  setSelectedCountry(country || null);
  
  // Call the parent's setActiveCountry to update the parent's selectedCountry state
  if (country) {
    setActiveCountry(country);
  }
  
  // Reset state and city selections since the country has changed
  setSelectedState(null);
  setSelectedCity(null);
  
  // Update the business address state with the new countryId and clear related fields
  setBusinessAddress((prev) => ({
    ...prev,
    countryId: countryId,
    stateId: -1,
    cityId: -1,
    street: "",
    zipcode: "",
  }));
};


  const handleStateChange = (
    bookingType: SelectChangeEvent<string>
  ) => {
    const stateId = Number(bookingType.target.value);
    const state = selectedCountry?.states?.find((s) => Number(s.id) === Number(stateId));
    setSelectedState(state || null);
    setSelectedCity(null);
    setBusinessAddress((prev) => ({
      ...prev,
      stateId: stateId,
      cityId: -1,
    }));
  };

  const handleCityChange = (
    bookingType: SelectChangeEvent<string>
  ) => {
    const cityId = Number(bookingType.target.value);
    const city = selectedState?.cities?.find((c) => Number(c.id) === Number(cityId));
    setSelectedCity(city || null);
    setBusinessAddress((prev) => ({
      ...prev,
      cityId: cityId,
    }));
  };

  const handleFieldChange = (
    field: keyof BusinessAddress,
    value: string | undefined
  ) => {
    setBusinessAddress((prev) => ({
      ...prev,
      [field]: value !== undefined ? value : "",
    }));
  };

  return (
    <Grid container spacing={4}>
      <Grid item xs={12} sm={4}>
        <FormControl variant="outlined" fullWidth disabled={disabled}>
          <InputLabel htmlFor="country">Country</InputLabel>
          <Select
            disabled={disabled}
            required
            id="country"
            name="country"
            label="Country"
            value={businessAddress?.countryId?.toString() || ""}
            onChange={handleCountryChange}
          >
            {countries?.map((country: Country) => (
              <MenuItem key={country.id} value={country.id}>
                {country.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={12} sm={4}>
        <FormControl variant="outlined" fullWidth disabled={disabled}>
          <InputLabel htmlFor="state">State</InputLabel>
          <Select
            required
            id="state"
            name="state"
            label="State"
            value={businessAddress?.stateId?.toString() || ""}
            onChange={handleStateChange}
            disabled={!selectedCountry && !disabled}
          >
            {selectedCountry?.states?.map((state) => (
              <MenuItem key={state.id} value={state.id}>
                {state.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={12} sm={4}>
        <FormControl variant="outlined" fullWidth disabled={disabled}>
          <InputLabel htmlFor="city">City</InputLabel>
          <Select
            required
            id="city"
            name="city"
            label="City"
            value={businessAddress?.cityId?.toString() || ""}
            onChange={handleCityChange}
            disabled={!selectedState && !disabled}
          >
            {selectedState?.cities?.map((city) => (
              <MenuItem key={city.id} value={city.id}>
                {city.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={12}>
        <TextField
          autoComplete="off"
          disabled={disabled}
          required
          label="Street Adddress"
          id="street"
          name="street"
          placeholder=" "
          variant="outlined"
          fullWidth
          value={businessAddress?.street || ""}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            handleFieldChange("street", e.target.value)
          }
        />
      </Grid>
      <Grid item xs={12} sm={4} marginBottom={"20px"}>
        <TextField
          disabled={disabled}
          required
          label="Zip Code"
          id="zipcode"
          name="zipcode"
          placeholder=" "
          variant="outlined"
          fullWidth
          value={businessAddress?.zipcode || ""}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            handleFieldChange("zipcode", e.target.value)
          }
        />
      </Grid>
    </Grid>
  );
};

export default BusinessAddressForm;
